import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * COARecordModal — one COA activity, presented EXACTLY the way the CMS CPE
 * protocol defines the Compliance Oversight Activities universe (Universe
 * Table 1, Columns A through L), with full text and no truncation.
 *
 * This is the read view. The wide grid answers "what is in the universe";
 * this answers "what does THIS record say", in the order and vocabulary an
 * auditor will use when they read the submitted universe row. Supplemental
 * fields the app tracks beyond the CMS layout (departments, remediation,
 * citations, measurements) are grouped separately and labeled as such, so
 * nobody mistakes them for submission columns.
 *
 * Measurements render as neutral meters (timely share of applicable cases)
 * with the status carried by badge + text, never by color alone.
 */

const API = process.env.REACT_APP_API_URL || '';

// CMS record layout: CCYY/MM/DD.
function cmsDate(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.replace(/-/g, '/') : s;
}

function pct(v) {
  if (v === null || v === undefined) return '—';
  return `${Number(v)}%`;
}

// CMS columns A–L in layout order. value() reads the activity; render marks
// prose fields that deserve paragraph treatment.
const CMS_COLUMNS = [
  { id: 'A', name: 'Component', value: (a) => a.component, hint: 'Department, operational area, or First Tier Entity that is the focus of the oversight activity' },
  { id: 'B', name: 'Activity Type', value: (a) => a.activity_type },
  { id: 'C', name: 'Compliance or FWA?', value: (a) => a.compliance_or_fwa },
  { id: 'D', name: 'Activity Frequency', value: (a) => a.activity_frequency },
  { id: 'E', name: 'Activity Rationale', value: (a) => a.activity_rationale, prose: true },
  { id: 'F', name: 'Activity Description', value: (a) => a.activity_description, prose: true },
  { id: 'G', name: 'Activity Start Date', value: (a) => cmsDate(a.activity_start_date) },
  { id: 'H', name: 'Activity Completion Date', value: (a) => cmsDate(a.activity_completion_date) || 'TBD' },
  { id: 'I', name: 'Number of Deficiencies', value: (a) => (a.number_of_deficiencies != null ? String(a.number_of_deficiencies) : 'TBD') },
  { id: 'J', name: 'Description of Deficiencies', value: (a) => a.description_of_deficiencies, prose: true },
  { id: 'K', name: 'Corrective Action Required', value: (a) => a.corrective_action_required },
  { id: 'L', name: 'Activity Results Shared?', value: (a) => a.activity_results_shared },
];

function StatTile({ label, value, sub, alert }) {
  return (
    <div className="rounded-xl border border-slate-200/60 bg-white px-4 py-3 min-w-[130px]">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${alert ? 'text-rose-700' : 'text-slate-900'}`}>
        {alert && <span aria-hidden className="mr-1">&#9888;</span>}{value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RateBadge({ value, deficiencies }) {
  if (value === null || value === undefined) {
    return <span className="text-slate-400 text-xs">no cases in scope</span>;
  }
  const bad = (deficiencies || 0) > 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-sm font-semibold ${bad ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
      {pct(value)}
      <span aria-hidden>{bad ? '⚠' : '✓'}</span>
    </span>
  );
}

// Neutral meter: timely share of applicable cases. Identity and status live in
// the adjacent text and badge; the bar only carries magnitude.
function Meter({ numerator, denominator }) {
  if (!denominator) return null;
  const width = Math.max(0, Math.min(100, (numerator / denominator) * 100));
  return (
    <div className="h-2 w-full rounded bg-slate-100 overflow-hidden" aria-hidden>
      <div className="h-full rounded bg-slate-500" style={{ width: `${width}%` }} />
    </div>
  );
}

export default function COARecordModal({ activity, onClose }) {
  const [full, setFull] = useState(activity);
  const [metrics, setMetrics] = useState(activity.metrics || []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/coa/activities/${activity.id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d.success || !d.activity) return;
        setFull((prev) => ({ ...prev, ...d.activity }));
        setMetrics(d.activity.metrics || []);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [activity.id]);

  const a = full;
  const isAgent = !!a.generated_by_agent;
  const totals = metrics.reduce((acc, m) => ({
    timely: acc.timely + (m.numerator || 0),
    applicable: acc.applicable + (m.denominator || 0),
    deficiencies: acc.deficiencies + (m.deficiency_count || 0),
    excluded: acc.excluded + (m.excluded_count || 0),
  }), { timely: 0, applicable: 0, deficiencies: 0, excluded: 0 });
  const overall = totals.applicable ? Math.round((totals.timely / totals.applicable) * 1000) / 10 : null;
  const groups = [...new Set(metrics.map((m) => m.metric_group || '—'))];
  const deptNames = (a.departments || []).map((d) => d.name).join(', ');

  // Portal to <body>: the app shell transforms its content pane, which would
  // re-anchor this fixed overlay to the pane and clip it at some window sizes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="w-[min(56rem,95vw)] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-slate-200/60"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200/60 px-6 py-4 rounded-t-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${a.status === 'completed' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}>{a.status || 'draft'}</span>
                {isAgent && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700 ring-1 ring-violet-200" title="Drafted by the monthly oversight agent; review and complete to publish">Agent draft</span>}
                {a.source_period && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">{a.source_period}</span>}
              </div>
              <h2 className="text-xl font-semibold text-slate-900 truncate">{a.component || 'COA activity'}</h2>
              {a.entity_name && <p className="text-sm text-slate-500 mt-0.5">{a.entity_name}</p>}
            </div>
            <button onClick={onClose} className="shrink-0 rounded-xl px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors">Close</button>
          </div>
        </div>

        <div className="px-6 py-5">
          {/* Headline numbers */}
          <div className="flex flex-wrap gap-3 mb-6">
            <StatTile
              label="Deficiencies (Column I)"
              value={a.number_of_deficiencies != null ? a.number_of_deficiencies : 'TBD'}
              sub={(() => {
                // A deficiency is a FINDING, and what a finding IS depends on
                // the activity: a missed benchmark for universe timeliness, a
                // late incident for privacy, an overdue memo for regulatory
                // change. "Untimely cases" was being stamped on all of them.
                const n = a.number_of_deficiencies;
                const subs = {
                  scrubber: [n === 1 ? 'benchmark missed' : 'benchmarks missed', 'all benchmarks met'],
                  privacy: ['privacy incidents past deadline', 'no privacy incidents past deadline'],
                  memo: ['memos past internal due date', 'all memos handled timely'],
                  cap: ['CAPs late or overdue', 'no CAP deficiencies'],
                  cil: ['issues past remediation target', 'no issue deficiencies'],
                  sentinel: ['deadlines missed', 'none identified'],
                };
                const pair = subs[a.source_app] || ['findings', 'none identified'];
                return n > 0 ? pair[0] : pair[1];
              })()}
              alert={a.number_of_deficiencies > 0}
            />
            <StatTile label="Corrective action (K)" value={a.corrective_action_required || 'TBD'} alert={a.corrective_action_required === 'Y'} />
            {overall !== null && (
              <StatTile label="Overall timeliness" value={pct(overall)} sub={`${totals.timely} of ${totals.applicable} cases timely`} />
            )}
            {totals.excluded > 0 && (
              <StatTile label="Excluded" value={totals.excluded} sub="dismissed / not assessable" />
            )}
          </div>

          {/* The CMS record, in submission order */}
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">CMS COA record</h3>
              <span className="text-xs text-slate-400">CPE protocol, Universe Table 1, Columns A–L</span>
            </div>
            <div className="rounded-xl border border-slate-200/60 divide-y divide-slate-100">
              {CMS_COLUMNS.map((col) => {
                const raw = col.value(a);
                const val = raw == null || raw === '' ? '—' : String(raw);
                return (
                  <div key={col.id} className="flex gap-4 px-4 py-3">
                    <div className="w-44 shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex w-5 h-5 items-center justify-center rounded bg-slate-100 text-[11px] font-semibold text-slate-600">{col.id}</span>
                        <span className="text-xs font-medium text-slate-600">{col.name}</span>
                      </div>
                      {col.hint && <p className="mt-1 text-[10px] leading-snug text-slate-400">{col.hint}</p>}
                    </div>
                    <div className={`min-w-0 flex-1 text-sm text-slate-800 ${col.prose ? 'whitespace-pre-wrap leading-relaxed' : ''}`}>
                      {val}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Measurements — per case type, when the activity carries them */}
          {metrics.length > 0 && (
            <div className="mb-6">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">Measurements by case type</h3>
                <span className="text-xs text-slate-400">computed at source; read-only</span>
              </div>
              <div className="rounded-xl border border-slate-200/60">
                {groups.map((group, gi) => (
                  <div key={group} className={gi > 0 ? 'border-t border-slate-200/60' : ''}>
                    <div className="px-4 pt-3 pb-1 text-xs font-semibold text-slate-700">{group}</div>
                    <div className="px-4 pb-3">
                      {metrics.filter((m) => (m.metric_group || '—') === group).map((m) => (
                        <div key={m.id || m.metric_key} className="py-2 border-b border-slate-100 last:border-b-0">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <span className="text-sm text-slate-800 min-w-0 truncate" title={m.metric_label}>{m.metric_label}</span>
                            <span className="flex items-center gap-2 shrink-0">
                              {m.deficiency_count > 0 && (
                                <span className="text-xs font-medium text-rose-700">{m.deficiency_count} untimely</span>
                              )}
                              <RateBadge value={m.value === null ? null : Number(m.value)} deficiencies={m.deficiency_count} />
                            </span>
                          </div>
                          <Meter numerator={m.numerator || 0} denominator={m.denominator || 0} />
                          <div className="mt-1 text-xs text-slate-500">
                            {m.denominator ? `${m.numerator} of ${m.denominator} timely` : 'no cases in scope'}
                            {m.excluded_count > 0 && `, ${m.excluded_count} excluded`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Supplemental fields — tracked by the app, NOT CMS columns */}
          <div className="mb-2">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">Supplemental (not part of the CMS layout)</h3>
            </div>
            <div className="rounded-xl border border-slate-200/60 divide-y divide-slate-100 text-sm">
              {[
                ['Departments', deptNames || '—'],
                ['Quarter', a.activity_quarter || '—'],
                ['Remediation activities', a.remediation_activities || '—', true],
                ['CFR references', a.cfr_references || '—', true],
                ['Chapter references', a.chapter_references || '—', true],
              ].map(([label, val, prose]) => (
                <div key={label} className="flex gap-4 px-4 py-2.5">
                  <div className="w-44 shrink-0 text-xs font-medium text-slate-600 pt-0.5">{label}</div>
                  <div className={`min-w-0 flex-1 text-slate-800 ${prose ? 'whitespace-pre-wrap leading-relaxed' : ''}`}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          {loading && <p className="text-xs text-slate-400 mt-2">Refreshing record…</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}
